import { jsxs, jsx } from 'react/jsx-runtime';
import { styled } from 'styled-components';
import { useMutationDeleteComment } from '../mutations/useMutationDeleteComment.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { getUserColorFromId } from '@fabric-app/utils/color';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { Conversation } from '@fabric-app/ui/components/Conversation';
import { SimplifiedIconDotMenu, DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { useResourceDataContextSafe } from '../../resource-detail/components/context/resourceDataContext.js';
import AvatarUser from '../../user/components/AvatarUser/AvatarUser.js';
import { IconPencil, IconTrash, IconDropdown14BrokenLink, IconDropdown14Location } from '@fabric-app/icons/svg';
import { useRef, useState, useEffect } from 'react';
import { useFabricEvent, dispatchFabricEvent } from '../../hooks/useFabricEvent.js';
import { useExpandedFdocContext } from '../../resource-detail/components/ExpandedFdoc/ExpandedFdocProvider.js';
import { getAnnotationText } from '../queries/getAnnotationText.js';
import { useIsCommentOrphan } from '../stores/orphanCommentsStore.js';
import { animateAnnotationFocus } from '../../annotation/constants/animations.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';

const CommentComponent = ({ comment, resourceId, onEdit }) => {
  const ref = useRef(null);
  const { data: user } = useQueryUserInfo();
  const { disabledActions } = useResourceDataContextSafe();
  const mutationDeleteComment = useMutationDeleteComment();
  const [isHovering, setIsHovering] = useState(false);
  const [isOrphan] = useIsCommentOrphan(comment.id, resourceId);
  const { focusCommentId, setFocusCommentId, setBottomAdditionalHeight } = useExpandedFdocContext();
  const [wasFocused, setWasFocused] = useState(false);
  useEffect(() => {
    if (focusCommentId === comment.id) {
      ref.current?.scrollIntoView({
        behavior: "smooth"
      });
      setFocusCommentId(null);
      setIsHovering(true);
      setWasFocused(true);
    }
  }, [
    focusCommentId,
    comment.id,
    setFocusCommentId
  ]);
  const deleteHandler = async () => {
    if (typeof comment === "string") return;
    mutationDeleteComment.mutate({
      resourceId,
      commentId: comment.id
    });
  };
  const owner = typeof comment === "string" ? {
    name: user?.name ?? "",
    pictureUrlCdn: user?.pictureUrlCdn || void 0,
    color: getUserColorFromId(user?.id ?? "")
  } : comment.user ? {
    name: comment.user.name || "",
    pictureUrlCdn: comment.user.pictureUrlCdn || void 0,
    color: getUserColorFromId(comment.user.id)
  } : {
    name: "Deleted user"
  };
  const canManageComment = !disabledActions.manageOthersComments || !disabledActions.addOrEditOwnComment && comment.user?.id === user?.id;
  const annotationText = getAnnotationText(comment.annotation);
  const { isMobileView } = useResponsive();
  const onAnnotationClick = () => {
    if (!comment.annotation || isOrphan) return;
    const annotation = comment.annotation;
    setBottomAdditionalHeight?.((prev) => Math.min(prev, 350));
    setTimeout(() => {
      dispatchFabricEvent("fabric-annotation-action-focus", {
        type: "focus-annotation",
        commentId: comment.id,
        resourceId,
        annotation,
        origin: "comments"
      });
    }, 100);
  };
  const onPointerEnter = () => {
    if (isMobileView) return;
    setIsHovering(true);
    dispatchFabricEvent("fabric-annotation-action-hover", {
      type: "hover-annotation",
      hover: {
        annotationId: comment.annotation?.notepad?.elementId,
        commentId: comment.id
      },
      origin: "comments",
      resourceId
    });
  };
  const onPointerLeave = () => {
    if (isMobileView) return;
    setIsHovering(false);
    dispatchFabricEvent("fabric-annotation-action-hover", {
      type: "hover-annotation",
      hover: null,
      origin: "comments",
      resourceId
    });
  };
  useFabricEvent("fabric-annotation-action-hover", (e) => {
    const { hover, origin } = e.detail;
    if (origin === "comments" || resourceId !== e.detail.resourceId) return;
    if (hover === null || hover?.commentId !== comment.id) {
      setIsHovering(false);
    } else if (hover && hover.commentId === comment.id) {
      setIsHovering(true);
    }
  });
  return /* @__PURE__ */ jsxs(CommentWrapper, {
    "data-testid": "comment",
    ref,
    children: [
      /* @__PURE__ */ jsxs(Conversation.ItemHeader, {
        children: [
          /* @__PURE__ */ jsx(AvatarUser, {
            user: owner,
            size: 24,
            color: owner.color
          }),
          /* @__PURE__ */ jsx(Conversation.ItemHeaderName, {
            children: owner.name
          }),
          typeof comment === "object" && !comment.isDraft && canManageComment && /* @__PURE__ */ jsxs(DropdownMenu.Base, {
            renderTriggerElement: ({ onOpenChange, open }) => /* @__PURE__ */ jsx(DropdownButton, {
              as: DropdownMenu.Trigger,
              onOpenChange,
              open
            }),
            children: [
              /* @__PURE__ */ jsx(DropdownMenu.Group, {
                children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
                  iconElement: /* @__PURE__ */ jsx(IconPencil, {}),
                  onClick: onEdit,
                  "data-testid": "comment-edit-button",
                  disabled: comment.user?.id !== user?.id,
                  children: "Edit"
                })
              }),
              /* @__PURE__ */ jsx(DropdownMenu.Group, {
                children: /* @__PURE__ */ jsx(DropdownMenu.Item, {
                  iconElement: /* @__PURE__ */ jsx(IconTrash, {}),
                  variant: "danger",
                  onClick: deleteHandler,
                  "data-testid": "comment-delete-button",
                  children: "Delete"
                })
              })
            ]
          })
        ]
      }),
      annotationText && /* @__PURE__ */ jsxs(CommentInfoTag, {
        hoverable: !isOrphan,
        alignSelf: "flex-start",
        "data-hovering": isHovering,
        onClick: onAnnotationClick,
        onPointerEnter,
        onPointerLeave,
        onAnimationEnd: () => setWasFocused(false),
        "data-focus": wasFocused,
        children: [
          isOrphan ? /* @__PURE__ */ jsx(IconDropdown14BrokenLink, {}) : /* @__PURE__ */ jsx(IconDropdown14Location, {}),
          annotationText.quoted && /* @__PURE__ */ jsx(Conversation.InfoTagText, {
            "data-quote": true,
            children: "“"
          }),
          /* @__PURE__ */ jsx(Conversation.InfoTagText, {
            children: annotationText.text
          }),
          annotationText.quoted && /* @__PURE__ */ jsx(Conversation.InfoTagText, {
            "data-quote": true,
            children: "”"
          })
        ]
      }),
      /* @__PURE__ */ jsx(Conversation.ItemBody, {
        markdown: comment.content,
        parserOptions: {
          autoLinks: true
        },
        "data-or-obscured": true
      })
    ]
  });
};
const DropdownButton = styled(SimplifiedIconDotMenu).withConfig({
  displayName: "Comment__DropdownButton",
  componentId: "sc-c0e60a62-0"
})([
  `width:1.625rem;height:1.25rem;background:transparent;overflow:visible;svg{rotate:90deg;scale:0.65;color:white;}&:before{content:'';display:block;width:2rem;height:2rem;position:absolute;}`,
  `{&:hover{background:rgba(255,255,255,0.1);}}opacity:0;`,
  `{opacity:1;height:1rem;}`,
  ``
], mediaHover, mediaMobile, (p) => p.open && "opacity: 1;");
const CommentWrapper = styled(Conversation.Item).withConfig({
  displayName: "Comment__CommentWrapper",
  componentId: "sc-c0e60a62-1"
})([
  ``,
  `{&:hover{`,
  `{opacity:1;}}}`
], mediaHover, DropdownButton);
const CommentInfoTag = styled(Conversation.InfoTag).withConfig({
  displayName: "Comment__CommentInfoTag",
  componentId: "sc-c0e60a62-2"
})([
  `&[data-focus='true']{`,
  `}`
], animateAnnotationFocus);

export { CommentComponent as default };
//# sourceMappingURL=Comment.js.map
